import { useAddTaskAttachmentMutation, useDeleteTaskAttachmentMutation, useGetTaskAttachmentList, useReviewAttachment } from "@/services";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { routes } from "@/routes/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema } from "./validations";
import { FILE, DESCRIPTION, SHOW_TO_CUSTOMER } from "./consts";
import { getRequiredFields, planStatus, removeEmptyFields, setEmptyFieldsToNull } from "@/lib/utils";
import { toast } from "@/components";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import moment from "moment";

const useAttachments = () => {
  const {id, action, planAction} = useParams();
  const queryClient = useQueryClient();
  const isView = planAction == routes.view || action == routes.view;
  const [activeTab, setActiveTab] = useState<string>("pm");
  const [attachmentData, setAttachmentData] = useState<any>("");
  const [openDeleteAttachmentDialog, setOpenDeleteAttachmentDialog] = useState<boolean>(false);
  const fileInputRef = useRef<any>(null);
  const form = useForm<any>({
    defaultValues: {
      [FILE]: "",
      [DESCRIPTION]: "",
      [SHOW_TO_CUSTOMER]: false
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);
  const {taskData, planData} = useSelector((state: any) => state.currentData);

  const isPlanApproved = useMemo(() => planData?.status == planStatus.approved.value, [planData]);
  
  const {data: attachments, isLoading: isAttachmentsLoading} = useGetTaskAttachmentList({taskId: taskData?.taskId, projectId: id as string, includePresignedUrls: true})

  const { mutate: addInfo, isPending: isAddPending } =
    useAddTaskAttachmentMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        form.reset({
          [FILE]: "",
          [DESCRIPTION]: "",
          [SHOW_TO_CUSTOMER]: false
        })
        queryClient.invalidateQueries({queryKey: ["attachment-list", taskData?.taskId]})
        toast({ message: "Attachment Added Successfully" });
      },
    });

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteTaskAttachmentMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        form.reset({
          [FILE]: "",
          [DESCRIPTION]: "",
          [SHOW_TO_CUSTOMER]: false
        })
        setOpenDeleteAttachmentDialog(false);
        queryClient.invalidateQueries({queryKey: ["attachment-list", taskData?.taskId]})
        toast({ message: "Attachment Deleted Successfully" });
      },
    });

  const { mutate: reviewAttachment, isPending: isReviewPending } = useReviewAttachment({
    onSuccess: () => {
      toast({ message: "Status Updated Successfully" });
      queryClient.invalidateQueries({queryKey: ["attachment-list", taskData?.taskId]})
    },
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
    }
  });

    
  const onSubmit = (data: any) => {
    const _data = setEmptyFieldsToNull(removeEmptyFields(data));
    
    if (!_data[FILE]) {
      form.setError(FILE, {message: 'This Field is required'});
      return;
    }

    addInfo({
      projectId: id as string,
      taskId: taskData?.taskId,
      data: {..._data},
    });
  }

  const onChangeTab = (currentTab: string) => {
    setActiveTab(currentTab)
  }

  const attachmentsByRole = useMemo(() => {
    let filteredData = [];
    if (activeTab == "pm") {
      filteredData = attachments?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "project manager")
    } else if (activeTab == "sm") {
      filteredData = attachments?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "site manager")
    } else if (activeTab == "qc") {
      filteredData = attachments?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "quality officer")
    } else if (activeTab == "sc") {
      filteredData = attachments?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "safety officer")
    }
    const convertedData = (filteredData || [])?.map((item: any) => ({
      ...item,
      createdAt: item.createdAt ? moment.utc(item.createdAt).utcOffset("+05:30").format() : item.createdAt
    }))

    return (convertedData || [])?.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [activeTab, attachments]);

  const onDeleteAttachment = (data: any) => {
    setAttachmentData(data)
    setOpenDeleteAttachmentDialog(true);
  }

  const onDeleteAttachmentConfirm = () => {
    deleteInfo({
      projectId: id as string,
      taskId: taskData?.taskId,
      attachmentId: attachmentData?.taskAttachmentId as string
    })
  }

  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "rejected">("approve");
  const [reason, setReason] = useState("");
  const [selectedAttachment, setSelectedAttachment] = useState<any>(null);

  const onPreviewPhoto = (url: string) => {
    setPreviewPhotoUrl(url);
  };
  
  const closePreview = () => {
    setPreviewPhotoUrl(null);
  };

  const onShowReviewConfirmation = (attachment: any, type: "approve" | "rejected") => {
    setSelectedAttachment(attachment);
    setActionType(type);
    setReason("");
    setShowConfirmation(true);
  };

  const handleReviewConfirm = () => {
    if (!selectedAttachment) return;
    
    reviewAttachment({
      projectId: id as string,
      taskId: taskData?.taskId,
      attachmentId: selectedAttachment.taskAttachmentId,
      payload: {
        action: actionType === "approve" ? "APPROVED" : "REJECTED",
        rejectionReason: actionType === "rejected" ? reason : undefined
      }
    });
    setShowConfirmation(false);
  };

  const max3Reached = useMemo(() => {
    return attachments?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "project manager")?.length == 3
  }, [attachments])

  return {
    attachments,
    isAttachmentsLoading,
    isView,
    form,
    requiredFields,
    onSubmit,
    isAddPending,
    attachmentsByRole: attachmentsByRole || [],
    activeTab,
    onChangeTab,
    fileInputRef,
    onDeleteAttachment,
    onDeleteAttachmentConfirm,
    openDeleteAttachmentDialog,
    setOpenDeleteAttachmentDialog,
    isDeletePending,
    isReviewPending,
    showConfirmation,
    setShowConfirmation,
    actionType,
    reason,
    setReason,
    onShowReviewConfirmation,
    handleReviewConfirm,
    previewPhotoUrl,
    onPreviewPhoto,
    closePreview,
    max3Reached,
    isPlanApproved
  };
};

export default useAttachments;
